import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Popover, ScrollArea } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { NodeViewWrapper, type NodeViewProps } from '@tiptap/react';
import { useState } from 'react';
import styled from 'styled-components';

import { useEditorFocusScope } from '../hooks/useEditorFocusScope';
import { conditionChoices } from '../utils/conditionVariables';
import { getVariableInfo } from '../utils/emailVariableCatalog';
import { useTemplateVariables } from '../utils/templateVariablesContext';
import { ConditionalMenuItem, ConditionalMenuLabel } from './conditionalControl.styled';

const Pill = styled.button`
	appearance: none;
	border: 0;
	margin: 0;
	background: none;
	color: inherit;
	font: inherit;
	text-align: left;
	cursor: pointer;
`;

export const LiquidVariablePill = ({ node, editor, getPos, updateAttributes }: NodeViewProps) => {
	const tMipManage = useTranslationMipManage();
	const templateVariables = useTemplateVariables();
	const attachFocusScope = useEditorFocusScope(editor);
	const [opened, setOpened] = useState(false);

	const name = String(node.attrs.name ?? '');
	const choices = conditionChoices(templateVariables, name);

	const openMenu = () => {
		if (!editor.isEditable) {
			return;
		}
		const position = getPos();
		if (typeof position !== 'number') {
			return;
		}
		editor.chain().focus(undefined, { scrollIntoView: false }).setNodeSelection(position).run();
		setOpened(true);
	};

	return (
		<NodeViewWrapper as="span" contentEditable={false}>
			<Popover
				opened={opened}
				onChange={setOpened}
				position="bottom-start"
				shadow="md"
				radius="md"
				width={256}
				withinPortal
			>
				<Popover.Target>
					<Pill
						type="button"
						className="liquid-var"
						title={name}
						aria-haspopup="dialog"
						onClick={openMenu}
					>
						{getVariableInfo(name).label}
					</Pill>
				</Popover.Target>
				<Popover.Dropdown ref={attachFocusScope} p={4}>
					<ConditionalMenuLabel>
						{tMipManage('email_templates.editor.replace_variable', {
							defaultValue: 'Replace with',
						})}
					</ConditionalMenuLabel>
					<ScrollArea.Autosize mah={280} type="auto">
						{choices.map((candidate) => (
							<ConditionalMenuItem
								key={candidate}
								type="button"
								onClick={() => {
									updateAttributes({ name: candidate });
									setOpened(false);
								}}
							>
								{getVariableInfo(candidate).label}
								{candidate === name && (
									<IconCheck
										size={14}
										color="var(--mantine-color-blue-6)"
										style={{ flexShrink: 0 }}
									/>
								)}
							</ConditionalMenuItem>
						))}
					</ScrollArea.Autosize>
				</Popover.Dropdown>
			</Popover>
		</NodeViewWrapper>
	);
};
